import { memo } from "react";
import type { FallbackTier, TrafficCorridor, TrafficCorridorLevel } from "@chula/shared";
import { PanelHeader } from "./PanelHeader";
import { PanelState } from "./PanelState";

/**
 * Corridor congestion — MODELED travel times for the 10 main campus
 * ingress/egress corridors (hour-of-day commute curve), enriched with LIVE
 * iTIC incidents near each corridor. Ratio = modeled time / free-flow time.
 *
 * Provenance is explicit: header says MODEL, tier is "scenario", and the
 * footer explains the method. Swaps to probe data with zero UI changes when
 * a real travel-time source lands (the feed contract stays the same).
 */

interface Props {
  corridors: TrafficCorridor[];
  ageMinutes: number;
  fallbackTier: FallbackTier | "loading";
}

// Congestion severity is a genuine data encoding (like AQI bands) — colors
// mirror the WorldStrip uv/aqi conventions.
const LEVEL_COLOR: Record<TrafficCorridorLevel, string> = {
  "free-flow": "var(--good)",
  light: "var(--text)",
  moderate: "var(--warn)",
  heavy: "var(--bad)",
  gridlock: "var(--crit)",
};

const LEVEL_LABEL: Record<TrafficCorridorLevel, string> = {
  "free-flow": "FREE",
  light: "LIGHT",
  moderate: "MOD",
  heavy: "HEAVY",
  gridlock: "GRIDLOCK",
};

function delayPct(ratio: number): string {
  const pct = Math.round((ratio - 1) * 100);
  return pct <= 0 ? "±0%" : `+${pct}%`;
}

function TrafficCorridorPanelImpl({ corridors, ageMinutes, fallbackTier }: Props) {
  const sorted = [...corridors].sort((a, b) => b.congestionRatio - a.congestionRatio);
  const worst = sorted[0];

  return (
    <div className="traffic-corridors">
      <PanelHeader
        title="CORRIDORS — MODEL"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier === "loading" ? null : fallbackTier}
        source="model+itic"
      />
      <p className="caption hint-sub">
        Estimated drive times on the main roads around campus.
        &ldquo;+40%&rdquo; means 40% slower than a clear road.
      </p>
      {fallbackTier === "loading" ? (
        <PanelState kind="loading" what="corridor travel times" />
      ) : corridors.length === 0 ? (
        <PanelState
          kind={fallbackTier === "unavailable" ? "error" : "empty"}
          what="corridor estimates"
          source="the corridor model"
          ageMinutes={ageMinutes}
        />
      ) : (
        <>
          {worst && worst.congestionRatio >= 1.4 && (
            <div className="corridor-worst mono" style={{ color: LEVEL_COLOR[worst.level] }}>
              WORST: {worst.label} {delayPct(worst.congestionRatio)}
            </div>
          )}
          <div className="corridor-rows">
            {sorted.map((c) => (
              <div key={c.id} className="corridor-row" title={`${c.distanceText} · free-flow ${Math.round(c.freeFlowSec / 60)} min`}>
                <span className="corridor-label">{c.label}</span>
                <span className="corridor-time mono">{c.durationText}</span>
                <span className="corridor-delay mono" style={{ color: LEVEL_COLOR[c.level] }}>
                  {delayPct(c.congestionRatio)}
                </span>
                <span className="corridor-level mono" style={{ color: LEVEL_COLOR[c.level] }}>
                  {LEVEL_LABEL[c.level]}
                </span>
              </div>
            ))}
          </div>
          <p className="corridor-foot caption">
            Hour-of-day commute model + live iTIC incidents within 500 m.
            Not probe data — will swap to a measured source when one lands.
          </p>
        </>
      )}
    </div>
  );
}

export const TrafficCorridorPanel = memo(TrafficCorridorPanelImpl);
